/**
 * Shared helpers for rendering V2 daily-report shape.
 * Kept in one place so Analysis (list), Dashboard (recent rows), and
 * ReportDetail all stay consistent on action/severity styling and on
 * how they pull tickers out of the structured output.
 */

export const actionBadge = (action) => {
  const a = (action || '').toUpperCase();
  if (a === 'BUY' || a === 'ADD') return 'badge-success';
  if (a === 'SELL' || a === 'REDUCE') return 'badge-danger';
  return 'badge-warning'; // HOLD, etc.
};

export const actionAccent = (action) => {
  const a = (action || '').toUpperCase();
  if (a === 'BUY' || a === 'ADD') return 'var(--action-buy)';
  if (a === 'SELL' || a === 'REDUCE') return 'var(--action-sell)';
  return 'var(--action-hold)';
};

export const confidenceBadge = (confidence) => {
  const c = (confidence || '').toLowerCase();
  if (c === 'high') return 'badge-success';
  if (c === 'low') return 'badge-danger';
  return 'badge-warning';
};

export const severityStyle = (severity) => {
  const s = (severity || 'info').toLowerCase();
  if (s === 'alert') return { background: 'var(--severity-alert-bg)', borderLeft: '4px solid var(--severity-alert-border)' };
  if (s === 'warn')  return { background: 'var(--severity-warn-bg)',  borderLeft: '4px solid var(--severity-warn-border)' };
  return                       { background: 'var(--severity-info-bg)',  borderLeft: '4px solid var(--severity-info-border)' };
};

/**
 * Compact ticker/theme chip palette. Centralized so dark mode can override
 * the underlying CSS tokens in one place.
 */
export const chipStyle = (color = 'blue') => {
  const palettes = {
    blue:   { background: 'var(--chip-blue-bg)',   color: 'var(--chip-blue-fg)' },
    purple: { background: 'var(--chip-purple-bg)', color: 'var(--chip-purple-fg)' },
  };
  return {
    display: 'inline-block',
    padding: '0.125rem 0.5rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '4px',
    ...(palettes[color] || palettes.blue),
  };
};

/**
 * Pull every actionable ticker out of a report's callsToAction, deduped,
 * preserving order. Returns objects with { ticker, action, cssBadge }.
 * Used by Analysis list cards and the Dashboard recent-analysis rows to
 * show "BUY NVDA, SELL TSLA" inline instead of an opaque "2 actions" count.
 */
export const collectActionableTickers = (report) => {
  const calls = (report?.structuredOutput?.callsToAction) || [];
  const seen = new Set();
  const out = [];
  for (const c of calls) {
    const t = (c?.ticker || '').toUpperCase().trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push({
      ticker: t,
      action: (c.action || '').toUpperCase(),
      cssBadge: actionBadge(c.action),
    });
  }
  return out;
};

/**
 * Pull every unique ticker mentioned anywhere in a report (callsToAction,
 * portfolioAlerts, tickerHighlights). Used as a chip strip at the top of
 * the detail view -- jump to "all the tickers this report touched".
 */
export const collectAllTickers = (report) => {
  const so = report?.structuredOutput || {};
  const sources = [
    ...(so.callsToAction || []),
    ...(so.portfolioAlerts || []),
    ...(so.tickerHighlights || []),
  ];
  const seen = new Set();
  const out = [];
  for (const item of sources) {
    const t = (item?.ticker || '').toUpperCase().trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
};

/**
 * Aggregate ticker activity across a list of reports. Returns one row per
 * unique ticker (only those that appear in callsToAction -- portfolioAlerts
 * and tickerHighlights are intentionally excluded since they're observations
 * rather than directional positions). Sorted by mention count desc, then
 * most-recently-seen.
 *
 * Each row:
 *   { ticker, mentions, dominantAction, dominantBadge, avgPriority,
 *     lastSeen (Date|null), reportIds (string[]) }
 *
 * dominantAction is the action seen most often (BUY / SELL / HOLD / ADD /
 * REDUCE). When tied, the most recent occurrence wins. dominantBadge is the
 * pre-resolved badge class for direct UI use.
 */
export const aggregateTickerActivity = (reports) => {
  const stats = new Map();

  // Walk newest-first so "most recent" tie-break is implicit on action choice.
  const sortedReports = [...(reports || [])].sort((a, b) => {
    const da = new Date(a.reportDate || a.generatedAt || 0).getTime();
    const db = new Date(b.reportDate || b.generatedAt || 0).getTime();
    return db - da;
  });

  for (const report of sortedReports) {
    const date = report.reportDate ? new Date(report.reportDate)
      : report.generatedAt ? new Date(report.generatedAt) : null;
    const calls = (report.structuredOutput?.callsToAction) || [];

    for (const call of calls) {
      const t = (call?.ticker || '').toUpperCase().trim();
      if (!t) continue;

      let s = stats.get(t);
      if (!s) {
        s = {
          ticker: t,
          mentions: 0,
          actions: {},          // action -> count
          firstSeenAction: null, // newest action wins (we walk newest-first)
          priorities: [],
          lastSeen: null,
          reportIds: [],
        };
        stats.set(t, s);
      }

      s.mentions += 1;
      s.reportIds.push(report.id);
      if (date && (!s.lastSeen || date > s.lastSeen)) s.lastSeen = date;

      const action = (call.action || '').toUpperCase();
      if (action) {
        s.actions[action] = (s.actions[action] || 0) + 1;
        if (s.firstSeenAction == null) s.firstSeenAction = action;
      }
      if (call.priority != null) s.priorities.push(Number(call.priority));
    }
  }

  return [...stats.values()].map((s) => {
    const sortedActions = Object.entries(s.actions).sort((a, b) => b[1] - a[1]);
    const top = sortedActions[0];
    const tied = sortedActions.length > 1 && sortedActions[0][1] === sortedActions[1][1];
    // Tie-break with the newest-first action we saw.
    const dominantAction = tied && s.firstSeenAction ? s.firstSeenAction : (top?.[0] || 'HOLD');
    const avgPriority = s.priorities.length
      ? s.priorities.reduce((a, b) => a + b, 0) / s.priorities.length
      : null;
    return {
      ticker: s.ticker,
      mentions: s.mentions,
      dominantAction,
      dominantBadge: actionBadge(dominantAction),
      avgPriority,
      lastSeen: s.lastSeen,
      reportIds: s.reportIds,
    };
  }).sort((a, b) => {
    if (b.mentions !== a.mentions) return b.mentions - a.mentions;
    return (b.lastSeen?.getTime() || 0) - (a.lastSeen?.getTime() || 0);
  });
};
